import shp from 'shpjs';
import { kml as kmlToGeoJSON, gpx as gpxToGeoJSON } from '@tmcw/togeojson';
import { unzipSync, gunzipSync } from 'fflate';
const ACCEPT_EXTENSIONS = ['.geojson', '.kml', '.kmz', '.gpx', '.zip', '.tar.gz', '.tgz', '.tar', '.gz', '.json'];
export function getFileExtension(name) {
    const lower = name.toLowerCase();
    if (lower.endsWith('.tar.gz'))
        return '.tar.gz';
    if (lower.endsWith('.tgz'))
        return '.tgz';
    const idx = lower.lastIndexOf('.');
    if (idx === -1)
        return '';
    return lower.substring(idx);
}
export function toLatLngArray(coords) {
    return coords.map(([lng, lat]) => ({ lat, lng }));
}
export function sanitizeCoordinates(coords) {
    if (!coords || coords.length === 0)
        return [];
    const epsilon = 1e-9;
    const eq = (a, b) => Math.abs(a.lat - b.lat) < epsilon && Math.abs(a.lng - b.lng) < epsilon;
    let result = coords.slice();
    if (result.length >= 2 && eq(result[0], result[result.length - 1])) {
        result.pop();
    }
    const dedup = [];
    for (const pt of result) {
        if (dedup.length === 0 || !eq(dedup[dedup.length - 1], pt))
            dedup.push(pt);
    }
    return dedup;
}
function ringArea(coords) {
    let sum = 0;
    for (let i = 0; i < coords.length; i++) {
        const [x1, y1] = coords[i];
        const [x2, y2] = coords[(i + 1) % coords.length];
        sum += x1 * y2 - x2 * y1;
    }
    return Math.abs(sum) / 2;
}
function normalizeToFeatures(geojson) {
    if (!geojson)
        return [];
    let normalized = geojson;
    if (Array.isArray(geojson)) {
        const firstFC = geojson.find((g) => g && g.type === 'FeatureCollection');
        normalized = firstFC || geojson[0];
    }
    if (!normalized)
        return [];
    if (normalized.type === 'FeatureCollection') {
        return normalized.features || [];
    }
    else if (normalized.type === 'Feature') {
        return [normalized];
    }
    else if (normalized.type) {
        return [{ type: 'Feature', geometry: normalized, properties: {} }];
    }
    return [];
}
export function extractPolygons(geojson) {
    var _a;
    const results = [];
    const features = normalizeToFeatures(geojson);
    for (const feature of features) {
        const geometry = feature === null || feature === void 0 ? void 0 : feature.geometry;
        if (!geometry)
            continue;
        const name = ((_a = feature.properties) === null || _a === void 0 ? void 0 : _a.name) || 'Unnamed Polygon';
        if (geometry.type === 'Polygon') {
            const rings = geometry.coordinates || [];
            if (rings.length > 0 && rings[0].length >= 3) {
                results.push({
                    name,
                    coordinates: toLatLngArray(rings[0]),
                    type: 'polygon'
                });
            }
        }
        else if (geometry.type === 'MultiPolygon') {
            const polys = geometry.coordinates || [];
            polys.forEach((poly, idx) => {
                const exterior = poly[0];
                if (exterior && exterior.length >= 3) {
                    results.push({
                        name: polys.length > 1 ? `${name} (${idx + 1})` : name,
                        coordinates: toLatLngArray(exterior),
                        type: 'polygon'
                    });
                }
            });
        }
    }
    return results;
}
export function extractLargestExteriorRing(geojson) {
    var _a;
    let geometry = null;
    if (!geojson)
        return null;
    if (Array.isArray(geojson)) {
        const firstFC = geojson.find((g) => g && g.type === 'FeatureCollection');
        geojson = firstFC || geojson[0];
    }
    if (geojson.type === 'FeatureCollection') {
        const candidates = ((_a = geojson.features) === null || _a === void 0 ? void 0 : _a.map((f) => f && f.geometry).filter(Boolean)) || [];
        geometry = candidates.find((g) => g.type === 'Polygon' || g.type === 'MultiPolygon') || candidates[0] || null;
    }
    else if (geojson.type === 'Feature') {
        geometry = geojson.geometry;
    }
    else if (geojson.type === 'Polygon' || geojson.type === 'MultiPolygon') {
        geometry = geojson;
    }
    if (!geometry)
        return null;
    if (geometry.type === 'Polygon') {
        const rings = geometry.coordinates || [];
        if (!rings.length)
            return null;
        const exterior = rings[0];
        return toLatLngArray(exterior);
    }
    if (geometry.type === 'MultiPolygon') {
        const polys = geometry.coordinates || [];
        if (!polys.length)
            return null;
        let best = null;
        let bestArea = -1;
        for (const poly of polys) {
            const exterior = poly[0];
            if (exterior && exterior.length >= 3) {
                const a = ringArea(exterior);
                if (a > bestArea) {
                    bestArea = a;
                    best = exterior;
                }
            }
        }
        return best ? toLatLngArray(best) : null;
    }
    return null;
}
// Extracts point features (placemark candidates) from a GeoJSON. Mirrors
// the pattern in extractLineStrings / extractPolygons: walks every
// feature, picks out Point + MultiPoint geometries, and returns a
// flattened list. Each ParsedGeometry's `coordinates` is a single-element
// array — one entry per resulting placemark — so callers can iterate
// uniformly across primitive types.
export function extractPoints(geojson) {
    var _a;
    const results = [];
    const features = normalizeToFeatures(geojson);
    for (const feature of features) {
        const geometry = feature === null || feature === void 0 ? void 0 : feature.geometry;
        if (!geometry)
            continue;
        const baseName = ((_a = feature.properties) === null || _a === void 0 ? void 0 : _a.name) || 'Unnamed Placemark';
        if (geometry.type === 'Point') {
            const coord = geometry.coordinates;
            if (Array.isArray(coord) && coord.length >= 2) {
                results.push({
                    name: baseName,
                    coordinates: toLatLngArray([coord]),
                    type: 'point',
                });
            }
        }
        else if (geometry.type === 'MultiPoint') {
            const coords = geometry.coordinates || [];
            coords.forEach((c, idx) => {
                if (Array.isArray(c) && c.length >= 2) {
                    results.push({
                        name: coords.length > 1 ? `${baseName} (${idx + 1})` : baseName,
                        coordinates: toLatLngArray([c]),
                        type: 'point',
                    });
                }
            });
        }
    }
    return results;
}
export function extractLineStrings(geojson) {
    var _a;
    const results = [];
    const features = normalizeToFeatures(geojson);
    for (const feature of features) {
        const geometry = feature === null || feature === void 0 ? void 0 : feature.geometry;
        if (!geometry)
            continue;
        const name = ((_a = feature.properties) === null || _a === void 0 ? void 0 : _a.name) || 'Unnamed Path';
        if (geometry.type === 'LineString') {
            const coords = geometry.coordinates || [];
            if (coords.length >= 2) {
                results.push({
                    name,
                    coordinates: toLatLngArray(coords),
                    type: 'linestring'
                });
            }
        }
        else if (geometry.type === 'MultiLineString') {
            const lines = geometry.coordinates || [];
            lines.forEach((line, idx) => {
                if (line.length >= 2) {
                    results.push({
                        name: lines.length > 1 ? `${name} (${idx + 1})` : name,
                        coordinates: toLatLngArray(line),
                        type: 'linestring'
                    });
                }
            });
        }
    }
    return results;
}
export function validateGeoFile(file) {
    const ext = getFileExtension(file.name);
    if (!ACCEPT_EXTENSIONS.includes(ext)) {
        if (ext === '.shp') {
            return 'Please upload a .zip containing .shp, .shx, .dbf, and .prj files.';
        }
        return 'Unsupported file type. Accepts .geojson, .json, .kml, .kmz, .gpx, .zip, .tar.gz/.tgz, or .tar shapefile bundles.';
    }
    const maxBytes = 200 * 1024 * 1024;
    if (file.size > maxBytes) {
        return 'File too large. Max size is 200MB.';
    }
    return null;
}
export async function parseGeoFile(file) {
    const validation = validateGeoFile(file);
    if (validation) {
        return { geojson: null, error: validation };
    }
    try {
        const ext = getFileExtension(file.name);
        let geo = null;
        if (ext === '.geojson' || ext === '.json') {
            const text = await file.text();
            geo = JSON.parse(text);
        }
        else if (ext === '.kml') {
            const text = await file.text();
            const { DOMParser } = window;
            const xml = new DOMParser().parseFromString(text, 'text/xml');
            geo = kmlToGeoJSON(xml);
        }
        else if (ext === '.gpx') {
            // GPX 1.1: <trk>/<rte> become LineString features (paths),
            // <wpt> become Point features (placemarks). togeojson handles
            // both shapes; downstream extract* helpers route each tool to
            // the geometry kind it cares about.
            const text = await file.text();
            const xml = new window.DOMParser().parseFromString(text, 'text/xml');
            geo = gpxToGeoJSON(xml);
        }
        else if (ext === '.kmz') {
            // KMZ is a zipped KML bundle. Convention is `doc.kml` at the
            // root, but Google Earth and other exporters sometimes name the
            // primary KML differently — accept the first `.kml` entry we
            // find. Embedded resources (icons, overlays) are ignored;
            // togeojson only consumes the geometry from the KML itself.
            const arrayBuf = await file.arrayBuffer();
            const entries = unzipSync(new Uint8Array(arrayBuf));
            const kmlName = Object.keys(entries).find(n => n.toLowerCase().endsWith('.kml'));
            if (!kmlName) {
                throw new Error('KMZ archive does not contain a .kml file.');
            }
            const text = new TextDecoder().decode(entries[kmlName]);
            const xml = new window.DOMParser().parseFromString(text, 'text/xml');
            geo = kmlToGeoJSON(xml);
        }
        else if (ext === '.zip' || ext === '.tar.gz' || ext === '.tgz' || ext === '.tar' || ext === '.gz') {
            const arrayBuf = await file.arrayBuffer();
            const buf = new Uint8Array(arrayBuf);
            const isGzip = buf.length >= 2 && buf[0] === 0x1f && buf[1] === 0x8b;
            const isZip = buf.length >= 2 && buf[0] === 0x50 && buf[1] === 0x4b;
            const buildFromEntries = async (entries) => {
                const shpEntry = entries.find(e => e.lower.endsWith('.shp'));
                if (!shpEntry)
                    throw new Error('Archive does not contain a .shp file.');
                const base = shpEntry.lower.replace(/\.shp$/, '');
                const dbfEntry = entries.find(e => e.lower === `${base}.dbf`);
                const shxEntry = entries.find(e => e.lower === `${base}.shx`);
                const prjEntry = entries.find(e => e.lower === `${base}.prj`);
                const cpgEntry = entries.find(e => e.lower === `${base}.cpg`);
                const shpInput = { shp: shpEntry.data };
                if (dbfEntry)
                    shpInput.dbf = dbfEntry.data;
                if (shxEntry)
                    shpInput.shx = shxEntry.data;
                if (prjEntry)
                    shpInput.prj = prjEntry.data;
                if (cpgEntry)
                    shpInput.cpg = cpgEntry.data;
                return await shp(shpInput);
            };
            if (ext === '.zip' || isZip) {
                try {
                    geo = await shp(buf);
                }
                catch (primaryErr) {
                    try {
                        const files = unzipSync(buf);
                        const entries = Object.keys(files).map(name => ({ name, lower: name.toLowerCase(), data: files[name] }));
                        geo = await buildFromEntries(entries);
                    }
                    catch (fallbackErr) {
                        const msg = (primaryErr === null || primaryErr === void 0 ? void 0 : primaryErr.message) || (fallbackErr === null || fallbackErr === void 0 ? void 0 : fallbackErr.message) || 'Failed to read ZIP.';
                        throw new Error(msg);
                    }
                }
            }
            else if (ext === '.tar.gz' || ext === '.tgz' || (isGzip && getFileExtension(file.name) !== '.gz')) {
                try {
                    const ungz = gunzipSync(buf);
                    const entries = [];
                    const block = 512;
                    let offset = 0;
                    const dec = new TextDecoder();
                    const readString = (start, len) => dec.decode(ungz.subarray(start, start + len)).replace(/\0+$/, '').trim();
                    const readOctal = (start, len) => {
                        const s = readString(start, len).replace(/[^0-7]/g, '');
                        return s ? parseInt(s, 8) : 0;
                    };
                    while (offset + block <= ungz.length) {
                        const header = ungz.subarray(offset, offset + block);
                        if (header.every(b => b === 0))
                            break;
                        const name = readString(offset, 100);
                        const size = readOctal(offset + 124, 12);
                        const typeflag = header[156];
                        offset += block;
                        const data = ungz.subarray(offset, offset + size);
                        if ((typeflag === 0 || typeflag === 48) && size >= 0) {
                            entries.push({ name, lower: name.toLowerCase(), data: data.slice() });
                        }
                        offset += Math.ceil(size / block) * block;
                    }
                    if (entries.length === 0)
                        throw new Error('Empty tar archive');
                    geo = await buildFromEntries(entries);
                }
                catch (_a) {
                    throw new Error('Failed to read .tar.gz/.tgz shapefile archive.');
                }
            }
            else if (ext === '.tar') {
                try {
                    const entries = [];
                    const block = 512;
                    let offset = 0;
                    const dec = new TextDecoder();
                    const readString = (start, len) => dec.decode(buf.subarray(start, start + len)).replace(/\0+$/, '').trim();
                    const readOctal = (start, len) => {
                        const s = readString(start, len).replace(/[^0-7]/g, '');
                        return s ? parseInt(s, 8) : 0;
                    };
                    while (offset + block <= buf.length) {
                        const header = buf.subarray(offset, offset + block);
                        if (header.every(b => b === 0))
                            break;
                        const name = readString(offset, 100);
                        const size = readOctal(offset + 124, 12);
                        const typeflag = header[156];
                        offset += block;
                        const data = buf.subarray(offset, offset + size);
                        if ((typeflag === 0 || typeflag === 48) && size >= 0) {
                            entries.push({ name, lower: name.toLowerCase(), data: data.slice() });
                        }
                        offset += Math.ceil(size / block) * block;
                    }
                    if (entries.length === 0)
                        throw new Error('Empty tar archive');
                    geo = await buildFromEntries(entries);
                }
                catch (_b) {
                    throw new Error('Failed to read .tar shapefile archive.');
                }
            }
            else if (ext === '.gz') {
                throw new Error('Single .gz file detected. Please provide a bundle containing .shp, .shx, .dbf (and .prj), e.g., as .zip or .tar.gz');
            }
            else {
                throw new Error('Unsupported archive format.');
            }
        }
        if (!geo) {
            throw new Error('Failed to parse file.');
        }
        return { geojson: geo };
    }
    catch (e) {
        const rawMessage = (e === null || e === void 0 ? void 0 : e.message) || '';
        const friendly = rawMessage.includes('but-unzip') || rawMessage.includes('unzip') || rawMessage.includes('inflate')
            ? 'Failed to read ZIP. Ensure the .zip contains a valid Shapefile set (.shp, .shx, .dbf, .prj) and is not corrupted.'
            : rawMessage || 'Failed to parse file.';
        return { geojson: null, error: friendly };
    }
}
export const ACCEPT_FILE_EXTENSIONS = ACCEPT_EXTENSIONS;
